import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import {
  pageSectionSubtitleSx,
  pageSectionTitleSx,
} from "../../../constants/pageSectionStyles";
import { brandContainedButtonSx } from "../../../constants/brandContainedButtonStyles";
import { useBrandThemeColors } from "../../../branding";
import arrowLeftIcon from "../../../assets/icon-left.svg";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { useGuardedAsyncCallback } from "../../../hooks/useGuardedAsyncCallback";
import { useAssessmentPlanFilterOptions } from "../../../hooks/useAssessmentPlanFilterOptions";
import { useGlobalConfig } from "../../../hooks/useGlobalConfig";
import { useOtoDimensionCandidates } from "../../../hooks/useOtoDimensionCandidates";
import { useAssessmentNameOptions } from "../../../hooks/useAssessmentNameOptions";
import { useHasPrivilegeStatus } from "../../../hooks/useHasPrivilege";
import { resolvePlanTypeDisplayName } from "../../../services/autoTaggingService";
import {
  createEmptyMatchConditionDraft,
  createOtoPlan,
  fetchOtoAssessmentRecordConcepts,
  fetchOtoPlanById,
  fetchOtoSideConfigurableConcepts,
  formatOtoWeightForInput,
  OTO_PLAN_CREATE_SUCCESS_TOAST,
  OTO_PLAN_EDIT_SUCCESS_TOAST,
  toOtoMatchConditions,
  updateOtoPlan,
  validateOtoMatchConditions,
  type OtoMatchConditionDraft,
  type OtoPlan,
} from "../../../services/objectToObjectPlanService";
import { OBJECT_TO_OBJECT_PRIVILEGES } from "../../../services/permissionsService";
import { buildAiPlanPath } from "../constants";
import DiscardEnhancedTaggingRuleChangesDialog from "../EnhancedTaggingRule/DiscardEnhancedTaggingRuleChangesDialog";
import AssessmentNameField from "../TaggingPlan/AssessmentNameField";
import {
  CLASSIFICATION_PROMPT_MAX_LENGTH,
  OTO_ASSESSMENT_TEMPLATE_OPTIONS,
  OTO_DEFAULT_ASSESSMENT_TEMPLATE,
  createEmptyClassificationPlanConfig,
  type ClassificationPlanConfigState,
} from "../TaggingPlan/classificationPlanConfigOptions";
import { CONCEPT_VALUE_ALL_OPTION } from "../constants";
import {
  buildAssessmentRecordItems,
  buildReferenceItemsFromObjectSubType,
  mapAssessmentTemplateToApi,
} from "../TaggingPlan/classificationPlanApiMapping";
import { parseObjectSubTypeCodes } from "../TaggingPlan/objectSubTypeCodes";
import type { ConceptValue } from "../../../services/regulationsService";
import type { AutoTaggingPlanDimensionItem } from "../../../services/autoTaggingService";
import ClassificationPlanConfigFields, {
  ClassificationPlanFlowArrow,
} from "../TaggingPlan/ClassificationPlanConfigFields";
import {
  ReferenceItemsCascadePicker,
  type ReferenceCascadeSelection,
  type ReferenceDimensionSelection,
} from "../TaggingPlan/ClassificationCascadePicker";
import type { ConceptFetcher } from "../../../hooks/useConfigurableConcepts";
import MatchConditionsEditor from "./MatchConditionsEditor";
import OtoPromptFieldWithDefault from "./OtoPromptFieldWithDefault";

/**
 * OTO Dimension catalog — `GET /assessment-plans/configurable-concepts`
 * (not Classification's `/auto-tagging/plans/configurable-concepts`).
 */
const fetchOtoReferenceConcepts: ConceptFetcher = (targetType) =>
  fetchOtoSideConfigurableConcepts("reference", targetType);

const fetchOtoImpactedConcepts: ConceptFetcher = (targetType) =>
  fetchOtoSideConfigurableConcepts("impacted", targetType);

const fetchOtoAssessmentRecordConceptOptions: ConceptFetcher = (targetType) =>
  fetchOtoAssessmentRecordConcepts(targetType);

/** OTO currently only supports Implementation. */
function normalizeOtoAssessmentTemplate(raw: string): string {
  const trimmed = raw.trim();
  if (
    trimmed &&
    OTO_ASSESSMENT_TEMPLATE_OPTIONS.some(
      (item) => item.toLowerCase() === trimmed.toLowerCase(),
    )
  ) {
    return OTO_DEFAULT_ASSESSMENT_TEMPLATE;
  }
  return OTO_DEFAULT_ASSESSMENT_TEMPLATE;
}

function createEmptyOtoPlanConfig(): ClassificationPlanConfigState {
  return {
    ...createEmptyClassificationPlanConfig(),
    assessmentTemplate: OTO_DEFAULT_ASSESSMENT_TEMPLATE,
  };
}

/** White content panel — no border, no shadow. */
const otoFormCardSx = {
  bgcolor: "#fff",
  borderRadius: "8px",
  border: "none",
  boxShadow: "none",
  p: 2.5,
};

type ObjectSideState = {
  objectType: string;
  objectSubType: string;
  objectTypeName?: string;
  objectSubTypeName?: string;
  referenceConceptCode?: string;
  referenceConceptDisplayName?: string;
  referenceDimensions?: ReferenceDimensionSelection[];
};

function emptySide(): ObjectSideState {
  return { objectType: "", objectSubType: "" };
}

/** Same shape Classification Add feeds into ReferenceItemsCascadePicker. */
function toReferenceCascadeValue(
  side: ObjectSideState,
): ReferenceCascadeSelection {
  return {
    objectType: side.objectType,
    objectSubType: side.objectSubType,
    objectTypeName: side.objectTypeName,
    objectSubTypeName: side.objectSubTypeName,
    referenceConceptCode: side.referenceConceptCode,
    referenceConceptDisplayName: side.referenceConceptDisplayName,
    referenceDimensions: side.referenceDimensions,
    conceptCode: "",
    conceptValues: [],
    allValues: false,
  };
}

function sideFromCascade(next: ReferenceCascadeSelection): ObjectSideState {
  return {
    objectType: next.objectType,
    objectSubType: next.objectSubType,
    objectTypeName: next.objectTypeName,
    objectSubTypeName: next.objectSubTypeName,
    referenceConceptCode: next.referenceConceptCode,
    referenceConceptDisplayName: next.referenceConceptDisplayName,
    referenceDimensions: next.referenceDimensions,
  };
}

function buildSideItems(side: ObjectSideState) {
  return buildReferenceItemsFromObjectSubType({
    objectSubType: side.objectSubType,
    objectSubTypeName: side.objectSubTypeName,
    referenceConceptCode: side.referenceConceptCode,
    referenceDimensions: side.referenceDimensions,
  });
}

function dimensionItemToSelection(
  item: AutoTaggingPlanDimensionItem,
): ReferenceDimensionSelection {
  if (item.all_values) {
    return {
      conceptCode: item.concept,
      conceptDisplayName: item.display_name?.trim() || item.concept,
      values: [],
      allValues: true,
    };
  }
  return {
    conceptCode: item.concept,
    conceptDisplayName: item.display_name?.trim() || item.concept,
    allValues: false,
    values: item.values.map(
      (value): ConceptValue => ({
        id: value.code,
        code: value.code,
        name: value.name.trim() || value.code,
        description: value.description ?? "",
      }),
    ),
  };
}

/** Hydrate cascade side from API items (preferred) or legacy subtype string. */
function sideFromPlanItems(
  objectType: string,
  objectTypeName: string,
  objectSubType: string,
  items: readonly AutoTaggingPlanDimensionItem[],
): ObjectSideState {
  if (items.length > 0) {
    const referenceDimensions = items.map(dimensionItemToSelection);
    const first = referenceDimensions[0]!;
    if (first.allValues) {
      return {
        objectType,
        objectTypeName,
        objectSubType: CONCEPT_VALUE_ALL_OPTION.code,
        objectSubTypeName: CONCEPT_VALUE_ALL_OPTION.name,
        referenceConceptCode: first.conceptCode,
        referenceConceptDisplayName: first.conceptDisplayName,
        referenceDimensions,
      };
    }
    return {
      objectType,
      objectTypeName,
      objectSubType: first.values.map((value) => value.code).join(","),
      objectSubTypeName: first.values
        .map((value) => value.name.trim() || value.code)
        .join(", "),
      referenceConceptCode: first.conceptCode,
      referenceConceptDisplayName: first.conceptDisplayName,
      referenceDimensions,
    };
  }

  const codes = parseObjectSubTypeCodes(objectSubType);
  if (codes.length === 0) {
    return { objectType, objectTypeName, objectSubType: "" };
  }

  const referenceDimensions: ReferenceDimensionSelection[] = [
    {
      conceptCode: "DocType",
      conceptDisplayName: "DocType",
      allValues: false,
      values: codes.map((code) => ({
        id: code,
        code,
        name: code,
        description: "",
      })),
    },
  ];
  return {
    objectType,
    objectTypeName,
    objectSubType,
    objectSubTypeName: codes.join(", "),
    referenceConceptCode: "DocType",
    referenceConceptDisplayName: "DocType",
    referenceDimensions,
  };
}

function buildOtoFormFingerprint(
  assessmentName: string,
  objectA: ObjectSideState,
  objectB: ObjectSideState,
  prompt: string,
  conditions: OtoMatchConditionDraft[],
  planConfig: ClassificationPlanConfigState,
): string {
  return JSON.stringify({
    assessmentName: assessmentName.trim(),
    objectA: {
      objectType: objectA.objectType.trim(),
      objectSubType: objectA.objectSubType.trim(),
      referenceConceptCode: objectA.referenceConceptCode ?? "",
      referenceDimensions: objectA.referenceDimensions ?? [],
    },
    objectB: {
      objectType: objectB.objectType.trim(),
      objectSubType: objectB.objectSubType.trim(),
      referenceConceptCode: objectB.referenceConceptCode ?? "",
      referenceDimensions: objectB.referenceDimensions ?? [],
    },
    prompt: prompt.trim(),
    matchingDimensions: toOtoMatchConditions(conditions),
    planConfig: {
      assessmentTemplate: planConfig.assessmentTemplate.trim(),
      updateAssessmentRecordConcept:
        planConfig.updateAssessmentRecordConcept.trim(),
      updateAssessmentRecords: [...planConfig.updateAssessmentRecords],
    },
  });
}

export default function AddEditObjectToObjectPlanPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { planId: planIdParam } = useParams<{ planId?: string }>();
  const isEditMode = Boolean(planIdParam);
  const planId = planIdParam?.trim() || "";
  const { showMessage } = useSnackbar();
  const { primaryColor } = useBrandThemeColors();
  const { has: canManageOtoPlan, loaded: privilegesLoaded } =
    useHasPrivilegeStatus(OBJECT_TO_OBJECT_PRIVILEGES.manage);
  const { config: tenantConfig } = useGlobalConfig();
  const maxDimensionsPerPlan =
    typeof tenantConfig?.max_dimensions_per_plan === "number" &&
    tenantConfig.max_dimensions_per_plan > 0
      ? tenantConfig.max_dimensions_per_plan
      : null;
  // Object types from `GET /assessment-plans/configurable-concepts`.
  // Do not load auto-tagging concepts here — OTO cascades use assessment-plans.
  const { planTypes } = useAssessmentPlanFilterOptions(undefined, {
    typesSource: "assessment-plans",
    loadConcepts: false,
  });
  const [assessmentNameMenuActive, setAssessmentNameMenuActive] = useState(false);
  const [assessmentNameReloadToken, setAssessmentNameReloadToken] = useState(0);
  const {
    options: assessmentNameOptions,
    loading: assessmentNameOptionsLoading,
  } = useAssessmentNameOptions({
    enabled: assessmentNameMenuActive,
    reloadToken: assessmentNameReloadToken,
  });

  const [assessmentName, setAssessmentName] = useState("");
  const [objectA, setObjectA] = useState<ObjectSideState>(emptySide);
  const [objectB, setObjectB] = useState<ObjectSideState>(emptySide);
  const [prompt, setPrompt] = useState("");
  const [planConfig, setPlanConfig] = useState<ClassificationPlanConfigState>(
    createEmptyOtoPlanConfig,
  );
  const [conditions, setConditions] = useState<OtoMatchConditionDraft[]>([
    createEmptyMatchConditionDraft(),
  ]);
  const [showValidation, setShowValidation] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(isEditMode);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  /** Null in edit mode until the plan has hydrated — avoids a false dirty flash. */
  const [baselineFingerprint, setBaselineFingerprint] = useState<string | null>(
    () =>
      isEditMode
        ? null
        : buildOtoFormFingerprint(
            "",
            emptySide(),
            emptySide(),
            "",
            [createEmptyMatchConditionDraft()],
            createEmptyOtoPlanConfig(),
          ),
  );

  const {
    options: dimensionOptions,
    loading: conceptsLoading,
    error: dimensionsError,
    ready: dimensionsReady,
  } = useOtoDimensionCandidates(objectA.objectType, objectB.objectType);

  // Only prune when the user changes Object A/B types — never on initial edit
  // hydrate, otherwise a candidates fetch that omits saved codes wipes the selects
  // (weights stay, Dimension shows "Select Dimension").
  const previousObjectTypesRef = useRef({ a: "", b: "" });
  const pendingDimensionPruneRef = useRef(false);

  useEffect(() => {
    const previous = previousObjectTypesRef.current;
    const typesChanged =
      Boolean(previous.a || previous.b) &&
      (previous.a !== objectA.objectType || previous.b !== objectB.objectType);
    previousObjectTypesRef.current = {
      a: objectA.objectType,
      b: objectB.objectType,
    };
    if (typesChanged) {
      pendingDimensionPruneRef.current = true;
    }

    if (!pendingDimensionPruneRef.current) return;
    if (!dimensionsReady || conceptsLoading) return;
    if (!objectA.objectType.trim() || !objectB.objectType.trim()) return;

    pendingDimensionPruneRef.current = false;
    const allowed = new Set(dimensionOptions.map((item) => item.code));
    setConditions((prev) => {
      let changed = false;
      const next = prev.map((row) => {
        if (!row.dimensionCode || allowed.has(row.dimensionCode)) return row;
        changed = true;
        return {
          ...row,
          dimensionCode: "",
          dimensionLabel: "",
        };
      });
      return changed ? next : prev;
    });
  }, [
    conceptsLoading,
    dimensionOptions,
    dimensionsReady,
    objectA.objectType,
    objectB.objectType,
  ]);

  const dimensionOptionsForEditor = useMemo(() => {
    const byCode = new Map(
      dimensionOptions.map((item) => [item.code, item] as const),
    );
    for (const row of conditions) {
      const code = row.dimensionCode.trim();
      if (!code || byCode.has(code)) continue;
      byCode.set(code, {
        code,
        name: row.dimensionLabel.trim() || code,
        availability: "both",
      });
    }
    return Array.from(byCode.values());
  }, [conditions, dimensionOptions]);

  const objectATypeName = resolvePlanTypeDisplayName(
    planTypes,
    objectA.objectType,
  );

  const hydrateFromPlan = useCallback((plan: OtoPlan) => {
    setAssessmentName(plan.name?.trim() || "");
    setObjectA(
      sideFromPlanItems(
        plan.objectAType,
        plan.objectATypeName,
        plan.objectASubType,
        plan.referenceItems,
      ),
    );
    setObjectB(
      sideFromPlanItems(
        plan.objectBType,
        plan.objectBTypeName,
        plan.objectBSubType,
        plan.impactedItems,
      ),
    );
    setPrompt(plan.prompt?.trim() || "");
    setPlanConfig({
      ...createEmptyOtoPlanConfig(),
      assessmentTemplate: normalizeOtoAssessmentTemplate(plan.assessmentType),
      updateAssessmentRecordConcept: plan.updateAssessmentRecordConcept,
      updateAssessmentRecords: [...plan.updateAssessmentRecord],
    });
    setConditions(
      plan.matchingDimensions.length > 0
        ? plan.matchingDimensions.map((item) => ({
            id: `cond-${item.dimensionCode}-${crypto.randomUUID()}`,
            dimensionCode: item.dimensionCode,
            dimensionLabel: item.dimensionLabel,
            weight: formatOtoWeightForInput(item.weight),
          }))
        : [createEmptyMatchConditionDraft()],
    );
  }, []);

  useEffect(() => {
    if (!isEditMode || !planId) return;

    const statePlan = (location.state as { plan?: OtoPlan } | null)?.plan;
    if (statePlan && statePlan.id === planId) {
      hydrateFromPlan(statePlan);
      setLoadingPlan(false);
      return;
    }

    let cancelled = false;
    setLoadingPlan(true);
    setLoadError(null);
    fetchOtoPlanById(planId)
      .then((plan) => {
        if (cancelled) return;
        if (!plan) {
          setLoadError("Object to Object not found.");
          return;
        }
        hydrateFromPlan(plan);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setLoadError(
          error instanceof Error ? error.message : "Failed to load plan.",
        );
      })
      .finally(() => {
        if (!cancelled) setLoadingPlan(false);
      });

    return () => {
      cancelled = true;
    };
  }, [hydrateFromPlan, isEditMode, location.state, planId]);

  useEffect(() => {
    if (!isEditMode) return;
    if (loadingPlan || loadError) return;
    if (baselineFingerprint !== null) return;
    setBaselineFingerprint(
      buildOtoFormFingerprint(
        assessmentName,
        objectA,
        objectB,
        prompt,
        conditions,
        planConfig,
      ),
    );
  }, [
    assessmentName,
    baselineFingerprint,
    conditions,
    isEditMode,
    loadError,
    loadingPlan,
    objectA,
    objectB,
    planConfig,
    prompt,
  ]);

  const currentFingerprint = buildOtoFormFingerprint(
    assessmentName,
    objectA,
    objectB,
    prompt,
    conditions,
    planConfig,
  );
  const isDirty =
    baselineFingerprint !== null && currentFingerprint !== baselineFingerprint;

  const conditionValidation = useMemo(
    () =>
      validateOtoMatchConditions(conditions, {
        maxDimensions: maxDimensionsPerPlan,
      }),
    [conditions, maxDimensionsPerPlan],
  );

  // Concept V3: Object alone is enough; Dimension / Value are optional.
  const referenceComplete = Boolean(objectA.objectType.trim());
  const impactComplete = Boolean(objectB.objectType.trim());
  const objectsReady = referenceComplete && impactComplete;

  const showConfigFields = objectsReady;
  // Prompt (API default) only after both Reference and Impact objects are set.
  const showPromptField = objectsReady;

  // Drop prompt when either side is cleared.
  useEffect(() => {
    if (!objectsReady && prompt) {
      setPrompt("");
    }
  }, [objectsReady, prompt]);

  const referenceError =
    showValidation && !referenceComplete
      ? "Please select Reference Items."
      : undefined;
  const impactError =
    showValidation && !impactComplete
      ? "Please select Impact Items."
      : undefined;

  const configErrors = {
    assessmentTemplate:
      showValidation &&
      showConfigFields &&
      !planConfig.assessmentTemplate.trim()
        ? "Please select assessment type."
        : undefined,
  };
  const promptError = undefined;

  const handleReferenceChange = (next: ReferenceCascadeSelection) => {
    const nextSide = sideFromCascade(next);
    setObjectA(nextSide);
    // A and B cannot share the same object type.
    if (nextSide.objectType && nextSide.objectType === objectB.objectType) {
      setObjectB(emptySide());
    }
  };

  const handleImpactChange = (next: ReferenceCascadeSelection) => {
    const nextSide = sideFromCascade(next);
    setObjectB(nextSide);
    if (nextSide.objectType && nextSide.objectType === objectA.objectType) {
      setObjectA(emptySide());
    }
  };

  const leavePage = useCallback(() => {
    navigate(buildAiPlanPath("objectToObjectPlan"));
  }, [navigate]);

  const handleClose = useCallback(() => {
    if (isDirty) {
      setDiscardConfirmOpen(true);
      return;
    }
    leavePage();
  }, [isDirty, leavePage]);

  const handleConfirmDiscard = useCallback(() => {
    setDiscardConfirmOpen(false);
    leavePage();
  }, [leavePage]);

  const handleSubmit = useGuardedAsyncCallback(async () => {
    if (submitting) return;
    setShowValidation(true);

    const trimmedName = assessmentName.trim();
    if (!trimmedName) {
      showMessage("Please enter Assessment Name.", "warning");
      return;
    }
    if (!referenceComplete) {
      showMessage("Please select Reference Items.", "warning");
      return;
    }
    if (!impactComplete) {
      showMessage("Please select Impact Items.", "warning");
      return;
    }
    if (!planConfig.assessmentTemplate.trim()) {
      showMessage("Please complete Assessment Configuration.", "warning");
      return;
    }

    const validation = validateOtoMatchConditions(conditions, {
      maxDimensions: maxDimensionsPerPlan,
    });
    if (!validation.valid) {
      showMessage("Please fix the highlighted fields.", "warning");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        name: trimmedName,
        objectAType: objectA.objectType.trim(),
        objectATypeName: resolvePlanTypeDisplayName(
          planTypes,
          objectA.objectType,
        ),
        objectBType: objectB.objectType.trim(),
        objectBTypeName: resolvePlanTypeDisplayName(
          planTypes,
          objectB.objectType,
        ),
        referenceItems: buildSideItems(objectA),
        impactedItems: buildSideItems(objectB),
        assessmentType: mapAssessmentTemplateToApi(planConfig.assessmentTemplate),
        assessmentRecordItems: buildAssessmentRecordItems(
          planConfig.updateAssessmentRecords,
          planConfig.updateAssessmentRecordConcept,
        ),
        matchingDimensions: toOtoMatchConditions(conditions),
        prompt: prompt.trim(),
      };

      if (isEditMode) {
        await updateOtoPlan(planId, payload);
        showMessage(OTO_PLAN_EDIT_SUCCESS_TOAST, "success");
      } else {
        await createOtoPlan(payload);
        showMessage(OTO_PLAN_CREATE_SUCCESS_TOAST, "success");
      }
      navigate(buildAiPlanPath("objectToObjectPlan"));
    } catch (error) {
      showMessage(
        error instanceof Error
          ? error.message
          : isEditMode
            ? "Failed to update Object to Object."
            : "Failed to create Object to Object.",
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  });

  if (privilegesLoaded && !canManageOtoPlan) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          gap: 1,
          px: 3,
        }}
      >
        <Typography variant="h6" color="text.secondary">
          Access Denied
        </Typography>
        <Typography variant="body2" color="text.secondary">
          You do not have permission to manage Object to Object.
        </Typography>
        <Button onClick={handleClose} sx={{ mt: 1 }}>
          Back to Classification AI Plan
        </Button>
      </Box>
    );
  }

  if (loadingPlan) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          gap: 1,
          px: 3,
        }}
      >
        <Typography variant="h6" color="text.secondary">
          Unable to load plan
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {loadError}
        </Typography>
        <Button onClick={handleClose} sx={{ mt: 1 }}>
          Back to Classification AI Plan
        </Button>
      </Box>
    );
  }

  const pageTitle = isEditMode
    ? "Edit Object to Object Plan"
    : "Add New Object to Object Plan";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        bgcolor: "#f9fafb",
      }}
    >
      <Box
        sx={{
          pt: 0,
          px: 0,
          pb: "16px",
          flexShrink: 0,
          bgcolor: "#fff",
          borderBottom: "1px solid #F0F0F0",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1.5,
          }}
        >
          <IconButton
            aria-label="Go back"
            onClick={handleClose}
            disabled={submitting}
            sx={{
              width: 32,
              height: 32,
              p: 1,
              flexShrink: 0,
              mt: 0.25,
            }}
          >
            <Box
              component="img"
              src={arrowLeftIcon}
              alt=""
              sx={{
                width: 16.25,
                height: 16,
                display: "block",
                objectFit: "contain",
              }}
            />
          </IconButton>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Breadcrumbs
              separator="›"
              sx={{
                mb: "4px",
                "& .MuiBreadcrumbs-separator": { color: "#9ca3af", mx: 1 },
              }}
            >
              <Link
                component="button"
                variant="body1"
                onClick={handleClose}
                sx={{
                  color: primaryColor,
                  textDecoration: "none",
                  cursor: "pointer",
                  fontSize: "0.875rem",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Classification AI Plan
              </Link>
              <Typography
                sx={{
                  color: "#374151",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                }}
              >
                {pageTitle}
              </Typography>
            </Breadcrumbs>
            <Typography sx={{ ...pageSectionTitleSx, mb: "4px" }}>
              {pageTitle}
            </Typography>
            <Typography sx={pageSectionSubtitleSx}>
              Configure object types and assessment dimensions to define how two
              objects are evaluated for relevance.
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          px: 3,
          py: 2.5,
        }}
      >
        <Box
          sx={{
            ...otoFormCardSx,
            mb: 2,
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
          }}
        >
          <AssessmentNameField
            value={assessmentName}
            onChange={setAssessmentName}
            options={assessmentNameOptions}
            loading={assessmentNameOptionsLoading}
            disabled={submitting}
            error={showValidation && !assessmentName.trim()}
            helperText={
              showValidation && !assessmentName.trim()
                ? "Assessment Name is required."
                : undefined
            }
            onOpen={() => {
              setAssessmentNameMenuActive(true);
              setAssessmentNameReloadToken((token) => token + 1);
            }}
          />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "minmax(0,1fr) 44px minmax(0,1fr)",
              },
              gap: { xs: 1.5, md: "28px" },
              // Top-align so helper/error under one cascade does not sink the other.
              alignItems: "start",
              mt: 1.75,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              {/*
                Cascade UX matches Classification Reference Items, but OTO data
                comes from `/assessment-plans/configurable-concepts`:
                Object list + Dimension list for the selected Object.
                Values still use concept-values pagination.
              */}
              <ReferenceItemsCascadePicker
                value={toReferenceCascadeValue(objectA)}
                onChange={handleReferenceChange}
                error={Boolean(referenceError)}
                helperText={referenceError}
                disabled={submitting}
                configurableOnly={false}
                typesSource="assessment-plans-reference"
                fetchConcepts={fetchOtoReferenceConcepts}
                excludedObjectTypes={
                  objectB.objectType ? [objectB.objectType] : []
                }
              />
            </Box>
            <ClassificationPlanFlowArrow />
            <Box sx={{ minWidth: 0 }}>
              <ReferenceItemsCascadePicker
                label="Impact Items"
                placeholder="Please select impact Items"
                value={toReferenceCascadeValue(objectB)}
                onChange={handleImpactChange}
                error={Boolean(impactError)}
                helperText={impactError}
                disabled={submitting}
                configurableOnly={false}
                typesSource="assessment-plans-impacted"
                fetchConcepts={fetchOtoImpactedConcepts}
                excludedObjectTypes={
                  objectA.objectType ? [objectA.objectType] : []
                }
              />
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            ...otoFormCardSx,
            mb: 2,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "14px",
              color: objectsReady ? "#0D0D0D" : "#99A1AF",
            }}
          >
            Assessment Configuration
          </Typography>
          {!objectsReady ? (
            <Box
              sx={{
                bgcolor: "#F9FAFB",
                borderRadius: "4px",
                height: 80,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 2,
                fontSize: "12px",
                color: "#99A1AF",
                textAlign: "center",
              }}
            >
              Assessment configuration will be available after selecting both
              objects.
            </Box>
          ) : (
            <>
              <ClassificationPlanConfigFields
                value={planConfig}
                objectType={objectA.objectType}
                objectTypeName={objectATypeName}
                disabled={submitting}
                showTaggingApproach={false}
                showExecution={false}
                showLayer={false}
                assessmentTemplateOptions={OTO_ASSESSMENT_TEMPLATE_OPTIONS}
                fetchAssessmentRecordConcepts={
                  fetchOtoAssessmentRecordConceptOptions
                }
                errors={configErrors}
                onChange={(patch) =>
                  setPlanConfig((prev) => ({
                    ...prev,
                    ...patch,
                    // OTO is Implementation-only for now.
                    assessmentTemplate:
                      normalizeOtoAssessmentTemplate(
                        patch.assessmentTemplate ?? prev.assessmentTemplate,
                      ),
                  }))
                }
              />
              {showPromptField ? (
                <OtoPromptFieldWithDefault
                  referenceObjectType={objectA.objectType}
                  impactedObjectType={objectB.objectType}
                  value={prompt}
                  onChange={setPrompt}
                  maxLength={CLASSIFICATION_PROMPT_MAX_LENGTH}
                  label="Prompt"
                  required={false}
                  error={Boolean(promptError)}
                  helperText={promptError}
                  disabled={submitting}
                />
              ) : null}
            </>
          )}
        </Box>

        <Box
          sx={{
            opacity: objectsReady ? 1 : 0.55,
            pointerEvents: objectsReady ? "auto" : "none",
            mb: 2,
          }}
        >
          <MatchConditionsEditor
            rows={conditions}
            dimensionOptions={dimensionOptionsForEditor}
            maxDimensions={maxDimensionsPerPlan}
            dimensionsLoading={conceptsLoading}
            dimensionsError={dimensionsError}
            showValidation={showValidation && objectsReady}
            formError={conditionValidation.formError}
            rowErrors={conditionValidation.rowErrors}
            onChange={setConditions}
          />
        </Box>
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          justifyContent: "flex-end",
          gap: 1.5,
          px: 3,
          py: 1.5,
          bgcolor: "#fff",
          borderTop: "1px solid #F0F1F3",
        }}
      >
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={submitting}
          sx={{ textTransform: "none", minWidth: 96 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          sx={{ ...brandContainedButtonSx, minWidth: 96 }}
        >
          {submitting ? "Submitting…" : isEditMode ? "Save" : "Submit"}
        </Button>
      </Box>

      <DiscardEnhancedTaggingRuleChangesDialog
        open={discardConfirmOpen}
        onCancel={() => setDiscardConfirmOpen(false)}
        onConfirm={handleConfirmDiscard}
      />
    </Box>
  );
}
